import { useCallback, useEffect, useState } from "react";
import { Sparkles, RefreshCw, TrendingUp, Tag, Truck, LayoutList } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import api, { errMsg } from "@/lib/api";
import { dateFr } from "@/lib/format";
import { t } from "@/i18n";

const ICONS = { pricing: Tag, ingredient: TrendingUp, supplier: Truck, menu: LayoutList };
const PRIO = { high: "bg-rose-500", medium: "bg-amber-500", low: "bg-emerald-500" };

export default function Recommendations({ hasRecipes, incomplete }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const generate = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.post("/recommendations/generate");
      setData(data);
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    api.get("/recommendations").then(({ data }) => {
      setData(data);
      if (!data.items?.length && hasRecipes) generate();
    });
  }, [hasRecipes, generate]);

  return (
    <div className="flex flex-col rounded-xl border bg-card p-5" data-testid="dashboard-ai-recommendations-card">
      <div className="mb-4 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <div className="rounded-md bg-[hsl(var(--ai)/0.12)] p-1.5"><Sparkles className="h-4 w-4 text-[hsl(var(--ai))]" /></div>
          <div>
            <h3 className="text-sm font-semibold">{t("recommendations.recommandations_ia")}</h3>
            {data?.generated_at && <p className="text-[11px] text-muted-foreground">{t("recommendations.mises_a_jour_le")} {dateFr(data.generated_at)}</p>}
          </div>
        </div>
        <Button variant="ghost" size="sm" onClick={generate} disabled={loading || !hasRecipes} data-testid="regenerate-recommendations-btn">
          <RefreshCw className={`h-3.5 w-3.5 ${loading ? "animate-spin" : ""}`} />
        </Button>
      </div>
      <div className="flex-1 space-y-3">
        {loading && [0, 1, 2].map((i) => <Skeleton key={i} className="h-20 w-full" />)}
        {!loading && !data?.items?.length && (
          <p className="text-sm text-muted-foreground">{incomplete ? t("recommendations.ajoutez_les_ingredients_de_vos_plats_pour") : t("recommendations.ajoutez_des_recettes_pour_recevoir_des_rec")}</p>
        )}
        {!loading && data?.items?.map((r, i) => {
          const Icon = ICONS[r.type] || Sparkles;
          return (
            <div key={i} className="rounded-lg border bg-background/50 p-3 fade-up" style={{ animationDelay: `${i * 60}ms` }} data-testid={`recommendation-item-${i}`}>
              <div className="mb-1 flex items-center gap-2">
                <span className={`h-1.5 w-1.5 rounded-full ${PRIO[r.priority] || PRIO.low}`} />
                <Icon className="h-3.5 w-3.5 text-muted-foreground" />
                <p className="text-sm font-semibold leading-tight">{r.title}</p>
              </div>
              <p className="text-[13px] leading-relaxed text-muted-foreground">{r.message}</p>
            </div>
          );
        })}
      </div>
    </div>
  );
}
